import { useEffect, useRef } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { validateWaypoints } from '@rideiq/api/services/Rides';
import { GroupedOption } from '@rideiq/hooks/useRiderOptions';
import { ToastProps } from '@rideiq/shared/stores/toastStore';
import { cloneDeep, isEqual } from 'lodash';

interface ValidatedRideSubmitOptions {
  getValues: UseFormGetValues<RideFormData>;
  ridersOnRide: GroupedOption[];
  onValidatingChange?: (validating: boolean) => void;
  addToast: (toast: ToastProps) => void;
  checkAgeIntegrity: (waypoints: WaypointFormType[], riders: GroupedOption[]) => boolean;
  finishSubmit: (data: RideFormData, waypoints: WaypointFormType[]) => void;
}

export function useValidatedRideSubmit({
  getValues,
  ridersOnRide,
  onValidatingChange,
  addToast,
  checkAgeIntegrity,
  finishSubmit,
}: ValidatedRideSubmitOptions) {
  const ridersOnRideRef = useRef(ridersOnRide);
  const validatingSubmit = useRef(false);
  const validationController = useRef<AbortController>();
  const mounted = useRef(true);
  const onValidatingChangeRef = useRef(onValidatingChange);
  const ageAdvisorySnapshot = useRef<{ waypoints: WaypointFormType[]; riders: GroupedOption[] }>();

  ridersOnRideRef.current = ridersOnRide;
  onValidatingChangeRef.current = onValidatingChange;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      validationController.current?.abort();
      if (validatingSubmit.current) {
        onValidatingChangeRef.current?.(false);
      }
    };
  }, []);

  const validateAndSubmit = async (data: RideFormData, skipAgeCheck = false) => {
    if (validatingSubmit.current) {
      return;
    }

    validatingSubmit.current = true;
    onValidatingChange?.(true);
    const snapshot = cloneDeep(getValues());
    const riderSnapshot = cloneDeep(ridersOnRide);
    const controller = new AbortController();

    validationController.current = controller;
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        resolve(null);
      }, 15000);
    });

    try {
      const response = await Promise.race([
        validateWaypoints({ waypoints: snapshot.waypointsAttributes }, controller.signal).catch(() => null),
        timeout,
      ]);

      if (!mounted.current) {
        return;
      }
      if (response?.type !== 'success' || !response.data?.waypoints?.length) {
        const errors =
          response?.type === 'error' && response.status && response.status >= 400 && response.status < 500
            ? response.error.errors.filter(
                (error) => typeof error === 'string' && error.trim() && !/^Error validating waypoints:/i.test(error)
              )
            : [];

        if (errors.length) {
          errors.forEach((message) => addToast({ severity: 'error', message }));
        } else {
          addToast({
            severity: 'error',
            message:
              'Unable to validate the ride. Please try saving again. If the issue continues, contact Safe Ride Support.',
          });
        }

        return;
      }
      if (!isEqual(snapshot, getValues()) || !isEqual(riderSnapshot, ridersOnRideRef.current)) {
        addToast({ severity: 'error', message: 'The ride changed while validating. Please save again.' });

        return;
      }

      const waypoints = response.data.waypoints;

      const advisoryMatches =
        skipAgeCheck &&
        isEqual(ageAdvisorySnapshot.current?.waypoints, waypoints) &&
        isEqual(ageAdvisorySnapshot.current?.riders, riderSnapshot);

      if (advisoryMatches || checkAgeIntegrity(waypoints, riderSnapshot)) {
        ageAdvisorySnapshot.current = undefined;
        finishSubmit(data, waypoints);
      } else {
        ageAdvisorySnapshot.current = cloneDeep({ waypoints, riders: riderSnapshot });
      }
    } finally {
      clearTimeout(timeoutId!);
      if (validationController.current === controller) {
        validationController.current = undefined;
      }
      validatingSubmit.current = false;
      if (mounted.current) {
        onValidatingChange?.(false);
      }
    }
  };

  return validateAndSubmit;
}
